import { useState, useEffect } from "react";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import api from "../../services/api";

function ActivityDetailsModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#754F89] to-[#4B2364] px-6 py-6">
          <div className="flex items-start justify-between">
            <div className="pr-4">
              <h3 className="text-white font-bold text-[28px] leading-tight">Request Details</h3>
              <p className="text-[#F3E8FFB2] text-[15px] mt-1 break-all">
                {data.timestamp ? new Date(data.timestamp).toLocaleString("en-US") : "-"} • {data.ip_address || "-"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 cursor-pointer border-none transition-colors shrink-0"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="col-span-2 bg-[#F8FAFC80] rounded-[16px] border border-[#F1F5F9] px-4 py-3.5 text-left">
              <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Endpoint</p>
              <p className="text-[14px] font-bold text-[#1E293B] font-mono break-all">{data.endpoint || "-"}</p>
            </div>
            <div className="bg-[#F8FAFC80] rounded-[16px] px-4 py-3.5 border border-[#F1F5F9]">
              <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Method & Status</p>
              <p className="text-[14px] font-bold text-[#1E293B]">{data.method || "-"} / {data.status_code || "-"}</p>
            </div>
            <div className="bg-[#F8FAFC80] rounded-[16px] px-4 py-3.5 border border-[#F1F5F9]">
              <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Response Time</p>
              <p className="text-[14px] font-bold text-[#1E293B]">{data.response_time_ms ? `${data.response_time_ms} ms` : "-"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const LIMIT = 10;

const derivePageLabel = (row) => {
  if (row.page) return String(row.page);
  const endpoint = String(row.endpoint || "").toLowerCase();
  if (endpoint.includes("dashboard")) return "Dashboard Activity Page";
  if (endpoint.includes("transaction")) return "Transaction Report";
  if (endpoint.includes("login") || endpoint.includes("session")) return "Login Activity Page";
  return "Activity Page";
};

const deriveActionLabel = (row) => {
  if (row.action) return String(row.action);
  const endpoint = String(row.endpoint || "").toLowerCase();
  const method = String(row.method || "").toUpperCase();
  if (endpoint.includes("export")) return "Exported CSV Report";
  if (endpoint.includes("login") || endpoint.includes("session")) return "System Authentication";
  if (method === "GET") return "Viewed Detailed Logs";
  return "Viewed Detailed Logs";
};

export default function DashboardActivity() {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [mapModal, setMapModal] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [dateError, setDateError] = useState("");

  const normalizePagination = (raw, fallbackPage, fallbackLimit, rowCount) => {
    const p = raw || {};
    const current = Number(p.current_page ?? p.page) || fallbackPage;
    const total = Number(p.total) || 0;
    const lastByTotal = total > 0 ? Math.ceil(total / fallbackLimit) : 0;
    const last = Number(p.last_page ?? p.total_pages) || lastByTotal || (rowCount === fallbackLimit ? current + 1 : current);
    return {
      current_page: current,
      last_page: Math.max(1, last),
      total,
      has_more: Boolean(p.has_more ?? current < Math.max(1, last)),
    };
  };

  const applyDateValues = (nextFrom, nextTo) => {
    if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
      setDateError("From date cannot be after To date.");
      return;
    }
    setDateError("");
    setAppliedFromDate(nextFrom || "");
    setAppliedToDate(nextTo || "");
    setPage(1);
  };

  const fetchActivity = async (pg = page) => {
    setLoading(true);
    try {
      const res = await api.get(`/activity/requests`, {
        params: {
          page: pg,
          limit: LIMIT,
          search: searchQuery || undefined,
          from_date: appliedFromDate || undefined,
          to_date: appliedToDate || undefined,
        },
      });
      const d = res.data?.data;
      const requestsArray = d?.requests || d?.data || d?.activity || (Array.isArray(d) ? d : []);
      const normalized = normalizePagination(d?.meta || d?.pagination, pg, LIMIT, requestsArray.length);
      setData(requestsArray);
      setPagination(normalized);
      setPage(normalized.current_page);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchActivity(page);
  }, [page, searchQuery, appliedFromDate, appliedToDate]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (appliedFromDate) params.append("from_date", appliedFromDate);
      if (appliedToDate) params.append("to_date", appliedToDate);
      if (searchQuery) params.append("search", searchQuery);

      const res = await api.get(`/activity/requests/export?${params.toString()}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `activity_requests_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      accessorKey: "timestamp",
      header: "Date & Time",
      Cell: ({ row }) => {
        const raw = row.original.timestamp || row.original.created_at;
        const dt = raw ? new Date(raw) : null;
        return (
          <div>
            <p className="text-[13px] font-semibold text-[#191C1D]">
              {dt ? dt.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "-"}
            </p>
            <p className="text-[11px] text-[#4C444E] mt-0.5">{dt ? dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</p>
          </div>
        );
      },
    },
    {
      accessorKey: "ip_address",
      header: "IP Address",
      Cell: ({ cell }) => (
        <span className="text-[12px] font-mono text-[#4C444E]">{cell.getValue() || "-"}</span>
      ),
    },
    {
      id: "performing_page",
      header: "Performing Page",
      Cell: ({ row }) => (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#ECE8F2] text-[#5B4A6D]">
          {derivePageLabel(row.original)}
        </span>
      ),
    },
    {
      id: "action",
      header: "Action",
      Cell: ({ row }) => (
        <span className="text-[13px] text-[#2B2B2B]">{deriveActionLabel(row.original)}</span>
      ),
    },
    {
      id: "view",
      header: "",
      Cell: ({ row }) => (
        <button
          onClick={() => setMapModal(row.original)}
          className="px-4 py-1.5 rounded-full bg-white border border-[#D9D9E0] text-[12px] font-semibold text-[#4B2364] cursor-pointer hover:bg-[#F8F7FB]"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#E8E8EB] rounded-[14px] p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-white rounded-[10px] px-3 py-2 min-w-[180px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="bg-transparent outline-none text-[13px] text-[#191C1D] w-full"
            />
          </div>

          <div className="flex items-center gap-2 bg-white rounded-[10px] px-3 py-2">
            <span className="text-[11px] font-semibold text-[#4C444E] uppercase">FROM</span>
            <DatePicker
              value={fromDate}
              onChange={(e) => {
                const nextFrom = e.target.value;
                setFromDate(nextFrom);
                applyDateValues(nextFrom, toDate);
              }}
              placeholder="mm/dd/yyyy"
              className="w-[105px]"
            />
          </div>

          <div className="flex items-center gap-2 bg-white rounded-[10px] px-3 py-2">
            <span className="text-[11px] font-semibold text-[#4C444E] uppercase">TO</span>
            <DatePicker
              value={toDate}
              onChange={(e) => {
                const nextTo = e.target.value;
                setToDate(nextTo);
                applyDateValues(fromDate, nextTo);
              }}
              placeholder="mm/dd/yyyy"
              className="w-[105px]"
            />
          </div>
        </div>

        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 bg-[#34094D] text-white text-[13px] font-semibold px-4 py-2 rounded-[10px] border-none cursor-pointer hover:opacity-90 disabled:opacity-60"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {exporting ? "Exporting..." : "Export PDF"}
        </button>
      </div>

      {dateError && <p className="text-[11px] text-red-500 font-medium mb-3">{dateError}</p>}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading && data.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#4B2364] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {loading && data.length > 0 && (
              <div className="px-4 py-2 text-xs text-gray-500">Refreshing data...</div>
            )}
            <DataTable columns={columns} data={data} />
            {!loading && data.length === 0 && (
              <div className="py-8 text-center text-[13px] text-[#7D747F]">No activity found.</div>
            )}
          </>
        )}

        {pagination && pagination.last_page > 1 && (
          <div className="flex items-center justify-center gap-3 px-6 py-3.5 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`text-xs px-3.5 py-1.5 rounded-lg border border-gray-200 transition-colors ${page === 1 ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"}`}
            >
              ? Prev
            </button>
            <span className="text-xs text-gray-500">Page {page} of {pagination.last_page}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.has_more}
              className={`text-xs px-3.5 py-1.5 rounded-lg border border-gray-200 transition-colors ${!pagination.has_more ? "bg-gray-50 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50 cursor-pointer"}`}
            >
              Next ?
            </button>
          </div>
        )}
      </div>

      {mapModal && <ActivityDetailsModal data={mapModal} onClose={() => setMapModal(null)} />}
    </>
  );
}

